import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Maximize2, Pen, Highlighter, Eraser, Trash2, X } from "lucide-react";
import { LessonStage } from "../components/LessonStage";
import { Mascot } from "../components/Mascot";
import { useSyncState } from "../hooks/useSyncState";
import { useSessionChannel } from "../hooks/useSessionChannel";
import GaseousStudentDisplay from "./GaseousStudentDisplay";
import JoinQR from "../components/JoinQR";
import { BluetoothPairButton } from "../components/BluetoothPairButton";
import { lesson as fallbackLesson, setActiveLessonId } from "../lib/lessonData";
import { getPresentation } from "../lib/lessons";
import { findStoryboardEntry, findMcqStoryboardEntry, findMcqExplanationStoryboardEntry, loadStoredStoryboard } from "../lib/storyboard";

const FRAGMENT_MS = 5500;
const POST_AUDIO_PAUSE_MS = 500;

const requestFullscreen = async (el) => {
    const target = el || document.documentElement;
    const fn =
        target.requestFullscreen ||
        target.webkitRequestFullscreen ||
        target.mozRequestFullScreen ||
        target.msRequestFullscreen;
    if (!fn) return false;
    try {
        await fn.call(target);
        return true;
    } catch {
        return false;
    }
};

const isFullscreen = () =>
    !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
    );

class PCMQueuePlayer {
    constructor(sampleRate = 24000) {
        this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        this.sampleRate = sampleRate;
        this.nextStartTime = this.audioCtx.currentTime;
        this.activeSources = [];
        this.onEnded = null;
        this.checkEndedTimer = null;
    }

    playChunk(base64Data) {
        try {
            if (this.audioCtx.state === "suspended") {
                this.audioCtx.resume();
            }
            const raw = atob(base64Data);
            const bytes = new Uint8Array(raw.length);
            for (let i = 0; i < raw.length; i++) {
                bytes[i] = raw.charCodeAt(i);
            }
            const int16 = new Int16Array(bytes.buffer);
            
            const float32 = new Float32Array(int16.length);
            for (let i = 0; i < int16.length; i++) {
                float32[i] = int16[i] / 32768.0;
            }

            const buffer = this.audioCtx.createBuffer(1, float32.length, this.sampleRate);
            buffer.copyToChannel(float32, 0);

            const source = this.audioCtx.createBufferSource();
            source.buffer = buffer;
            source.connect(this.audioCtx.destination);

            const startTime = Math.max(this.audioCtx.currentTime, this.nextStartTime);
            source.start(startTime);
            this.activeSources.push(source);
            this.nextStartTime = startTime + buffer.duration;

            if (this.checkEndedTimer) clearTimeout(this.checkEndedTimer);
            const remainingTimeMs = (this.nextStartTime - this.audioCtx.currentTime) * 1000;
            this.checkEndedTimer = setTimeout(() => {
                if (this.onEnded) this.onEnded();
            }, remainingTimeMs + 200);

        } catch (e) {
            console.error("Error playing audio chunk", e);
        }
    }

    stop() {
        if (this.checkEndedTimer) clearTimeout(this.checkEndedTimer);
        this.activeSources.forEach(source => {
            try {
                source.stop();
            } catch (e) {}
        });
        this.activeSources = [];
        try {
            this.audioCtx.close();
        } catch (e) {}
    }
}

/**
 * Student Display — render-only view of the lesson stage.
 *
 * Two audio defenses prevent the "double audio" problem:
 *   1) Only one student window may "own" audio playback at a time. Each
 *      window broadcasts a claim with a unique id + timestamp on mount;
 *      older windows step down and mute themselves when they see a newer
 *      claim on the same BroadcastChannel.
 *   2) When the tab is hidden (backgrounded or another tab is focused),
 *      audio is paused — covers the case where a user accidentally has
 *      two student windows on the same machine.
 */
const StudentDisplay = () => {
    const [state, set] = useSyncState();
    const [showFsButton, setShowFsButton] = useState(false);
    const [hasAudioOwnership, setHasAudioOwnership] = useState(true);
    // Local state for AI speaking overlay — separate from shared `state.aiPaused`
    // so the blur+mascot overlay can animate independently of the sync state.
    const [aiOverlayState, setAiOverlayState] = useState(null);
    const sessionIdFromUrl = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("sessionId");
    }, []);
    const [lessonId, setLessonId] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("lessonId") || "photosynthesis";
    });

    const activeLesson = useMemo(() => {
        try {
            const p = getPresentation(lessonId);
            return p?.lesson || fallbackLesson;
        } catch {
            return fallbackLesson;
        }
    }, [lessonId]);

    const sessionChannel = useSessionChannel(sessionIdFromUrl);
    const pcmPlayerRef = useRef(null);
    const autopilotTimerRef = useRef(null);
    const safetyTimerRef = useRef(null);
    const playbackStateRef = useRef({
        slideIndex: -1,
        fragmentIndex: -1,
        audioSrc: null,
        started: false,
        ended: false,
    });
    const playStartedAtRef = useRef(null);
    const remainingMsRef = useRef(0);

    const isMatchingAudio = useCallback((eventSrc, entryAudio) => {
        if (!eventSrc || !entryAudio) return false;
        const clean = (s) => s.replace(/^(https?:)?\/\/[^\/]+/, "").replace(/^\//, "");
        return clean(eventSrc) === clean(entryAudio);
    }, []);

    /* Listen for real-time streaming AI voice response packets and orchestrate
       the smartboard's PCM queue playback and Mascot speaking animations */
    useEffect(() => {
        if (!sessionChannel?.onMessage) return;

        const unsubscribe = sessionChannel.onMessage((m) => {
            if (m?.type === "AI_SESSION_START") {
                // Pause any existing native lesson audio
                const storyboardAudio = document.querySelector('[data-testid="storyboard-audio"]');
                if (storyboardAudio) {
                    try {
                        storyboardAudio.pause();
                    } catch (e) {}
                }

                // Cancel any ongoing fallback speech synthesis
                try {
                    if (window.speechSynthesis) {
                        window.speechSynthesis.cancel();
                    }
                } catch (e) {}

                if (pcmPlayerRef.current) {
                    pcmPlayerRef.current.stop();
                }

                // Activate blur overlay + centered mascot on the student display in default state
                setAiOverlayState("idle");
                set({ mascotState: "idle", aiPaused: true });
            } else if (m?.type === "AI_AUDIO_LISTEN") {
                setAiOverlayState("listening");
                set({ mascotState: "listening" });
            } else if (m?.type === "AI_AUDIO_THINK") {
                setAiOverlayState("thinking");
                set({ mascotState: "thinking" });
            } else if (m?.type === "AI_AUDIO_START") {
                setAiOverlayState("speaking");
                set({ mascotState: "speaking", aiPaused: true });

                const player = new PCMQueuePlayer(24000);
                player.onEnded = () => {
                    // Audio finished playing — hide overlay and restore lesson
                    setAiOverlayState(null);
                    set({ mascotState: "idle", aiPaused: false });
                };
                pcmPlayerRef.current = player;
            } else if (m?.type === "AI_AUDIO_CHUNK") {
                if (pcmPlayerRef.current) {
                    pcmPlayerRef.current.playChunk(m.payload);
                }
            } else if (m?.type === "AI_AUDIO_END") {
                // Playback completion handled gracefully via player's onEnded callback
            } else if (m?.type === "AI_AUDIO_STOP") {
                if (pcmPlayerRef.current) {
                    pcmPlayerRef.current.stop();
                    pcmPlayerRef.current = null;
                }
                // Immediately hide the overlay and restore lesson
                setAiOverlayState(null);
                set({ mascotState: "idle", aiPaused: false });
            }
        });

        return () => {
            unsubscribe();
            if (pcmPlayerRef.current) {
                pcmPlayerRef.current.stop();
                pcmPlayerRef.current = null;
            }
            try {
                if (window.speechSynthesis) {
                    window.speechSynthesis.cancel();
                }
            } catch (e) {}
        };
    }, [sessionChannel, set]);

    /* Listen for broadcasts over the WebSocket session relay:
       - Handles REQUEST_STATE: responds to late-joining peers (like the mobile remote) by immediately sending current state.
       - Handles STATE_SYNC: applies changes to the smartboard's local state, filtering out tool choices and annotations (local-only by design). */
    useEffect(() => {
        if (!sessionChannel?.onMessage) return;
        return sessionChannel.onMessage((m) => {
            if (m?.type === "REQUEST_STATE") {
                if (sessionChannel?.send && state.sessionStarted) {
                    const { annotations, ...lightweightState } = state;
                    sessionChannel.send({
                        type: "STATE_SYNC",
                        role: "student-display",
                        lessonId: lessonId,
                        payload: lightweightState,
                    });
                }
            } else if (m?.type === "STATE_SYNC") {
                if (m.lessonId && m.lessonId !== lessonId) {
                    setLessonId(m.lessonId);
                    setActiveLessonId(m.lessonId);
                }
                if (m.payload) {
                    set((prev) => {
                        const {
                            annotationTool: _t,
                            annotationColor: _c,
                            annotations: _a,
                            ...rest
                        } = m.payload;

                        // Only trigger update if there is a real difference to avoid infinite loops
                        const hasChange = Object.keys(rest).some(
                            (k) => prev[k] !== rest[k]
                        );
                        if (!hasChange) return prev;

                        return {
                            ...prev,
                            ...rest,
                            // Keep this board's own tool/colour + local strokes.
                            annotationTool: prev?.annotationTool || "off",
                            annotationColor: prev?.annotationColor || "#5BC2CC",
                            annotations: prev?.annotations || {},
                        };
                    });
                }
            }
        });
    }, [sessionChannel, set, lessonId, state]);

    /* Annotations drawn on the student smartboard intentionally stay LOCAL.
       Per UX requirement: writing at the student end should NOT be mirrored
       to the teacher panel — the teacher's screen is just for guidance,
       students/teachers writing on the interactive panel should not pollute
       the teacher's view. */

    /* ─────────────── Fragment auto-advance ───────────────
       Runs ON THE STUDENT BOARD itself so playback keeps marching even when
       no teacher panel is open (e.g. Mobile-mode sessions where the teacher
       drives slides from their phone — the panel is never opened). The
       teacher panel runs the same effect; both writes are idempotent (whoever
       fires first wins, the other re-derives on the next render).

       At the last fragment we DON'T auto-advance slides — the teacher (laptop
       or mobile) controls slide-to-slide flow. We just play out the current
       slide one fragment at a time. */
    const storyboard = useMemo(() => {
        try {
            return loadStoredStoryboard(lessonId);
        } catch {
            return null;
        }
    }, [lessonId]);



    const totalFragments =
        activeLesson.slides[state.slideIndex]?.fragments?.length || 0;

    const advance = useCallback(() => {
        const total =
            activeLesson.slides[state.slideIndex]?.fragments?.length ||
            0;
        let advancedPatch = null;

        if (state.fragmentIndex < total - 1) {
            advancedPatch = {
                fragmentIndex: state.fragmentIndex + 1,
            };
        } else if (state.autoPilotEnabled) {
            const nextIdx = state.slideIndex + 1;
            const nextSlide = activeLesson.slides[nextIdx];
            if (nextSlide) {
                advancedPatch = {
                    slideIndex: nextIdx,
                    fragmentIndex: 0,
                    mode: nextSlide.mode,
                    currentSection: nextSlide.section,
                    slideRenderMode: "play",
                    revealAnswer: false,
                    revealMcqAnswer: false,
                    revealExpected: false,
                    revealCommonMistake: false,
                    solutionStep: 0,
                };
            }
        }

        if (advancedPatch) {
            set(advancedPatch);
            if (sessionChannel?.send) {
                try {
                    sessionChannel.send({
                        type: "STATE_SYNC",
                        role: "student-autopilot",
                        lessonId: lessonId,
                        payload: advancedPatch,
                    });
                } catch {
                    /* noop */
                }
            }
        }
    }, [state.slideIndex, state.fragmentIndex, state.autoPilotEnabled, set, sessionChannel, activeLesson, lessonId]);

    const handleBluetoothCommand = useCallback((command) => {
        if (!state) return;
        const cmd = command.trim().toUpperCase();
        let patch = null;

        if (cmd === "NEXT") {
            const total = activeLesson.slides[state.slideIndex]?.fragments?.length || 0;
            if (state.fragmentIndex < total - 1) {
                patch = { fragmentIndex: state.fragmentIndex + 1 };
            } else {
                const nextIdx = state.slideIndex + 1;
                const nextSlide = activeLesson.slides[nextIdx];
                if (nextSlide) {
                    patch = {
                        slideIndex: nextIdx,
                        fragmentIndex: 0,
                        mode: nextSlide.mode,
                        currentSection: nextSlide.section,
                        slideRenderMode: "play",
                    };
                }
            }
        } else if (cmd === "PREV") {
            if (state.fragmentIndex > 0) {
                patch = { fragmentIndex: state.fragmentIndex - 1 };
            } else if (state.slideIndex > 0) {
                const prevIdx = state.slideIndex - 1;
                const prevSlide = activeLesson.slides[prevIdx];
                if (prevSlide) {
                    patch = {
                        slideIndex: prevIdx,
                        fragmentIndex: 0,
                        mode: prevSlide.mode,
                        currentSection: prevSlide.section,
                        slideRenderMode: "play",
                    };
                }
            }
        }

        if (patch) {
            set(patch);
            if (sessionChannel?.send) {
                try {
                    sessionChannel.send({
                        type: "STATE_SYNC",
                        role: "student-bluetooth",
                        lessonId: lessonId,
                        payload: patch,
                    });
                } catch {}
            }
        }
    }, [state, set, activeLesson, sessionChannel, lessonId]);

    const handleAudioStarted = useCallback((src) => {
        const pb = playbackStateRef.current;
        if (
            pb.slideIndex === state.slideIndex &&
            pb.fragmentIndex === state.fragmentIndex &&
            pb.audioSrc
        ) {
            pb.started = true;
            if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
            safetyTimerRef.current = setTimeout(() => {
                console.warn("[autopilot] Active audio safety timeout fired. Advancing...");
                advance();
            }, 60000);
        }
    }, [state.slideIndex, state.fragmentIndex, advance]);

    const handleAudioEnded = useCallback((src) => {
        const pb = playbackStateRef.current;
        if (
            pb.slideIndex === state.slideIndex &&
            pb.fragmentIndex === state.fragmentIndex &&
            pb.audioSrc &&
            !pb.ended
        ) {
            pb.ended = true;
            if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
            if (autopilotTimerRef.current) clearTimeout(autopilotTimerRef.current);

            autopilotTimerRef.current = setTimeout(() => {
                advance();
            }, POST_AUDIO_PAUSE_MS);
        }
    }, [state.slideIndex, state.fragmentIndex, advance]);

    const handleAudioError = useCallback((src) => {
        const pb = playbackStateRef.current;
        if (
            pb.slideIndex === state.slideIndex &&
            pb.fragmentIndex === state.fragmentIndex &&
            pb.audioSrc
        ) {
            console.warn("[autopilot] Audio error. Falling back to static timer.");
            if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
            if (autopilotTimerRef.current) clearTimeout(autopilotTimerRef.current);
            autopilotTimerRef.current = setTimeout(() => {
                advance();
            }, FRAGMENT_MS);
        }
    }, [state.slideIndex, state.fragmentIndex, advance]);

    const handleAudioMetadata = useCallback((src, duration) => {
        if (duration && isFinite(duration)) {
            const durationMs = Math.round(duration * 1000);
            console.log(`[autopilot] Measured audio duration dynamically: ${durationMs}ms`);
            
            // If the calculated duration in remainingMsRef is currently 0, default 10s, or safety 60s, update it dynamically!
            if (!remainingMsRef.current || remainingMsRef.current === 10000 || remainingMsRef.current === 60000 || remainingMsRef.current === 0) {
                remainingMsRef.current = durationMs;
                
                // If the timer is currently running, reschedule the safety fallback timer dynamically!
                if (autopilotTimerRef.current && playStartedAtRef.current) {
                    clearTimeout(autopilotTimerRef.current);
                    const elapsed = Date.now() - playStartedAtRef.current;
                    const remaining = Math.max(0, durationMs - elapsed);
                    autopilotTimerRef.current = setTimeout(() => {
                        console.log(`[autopilot] Safety fallback reached after dynamic measurement (${durationMs}ms + 4000ms). Advancing...`);
                        advance();
                    }, remaining + 4000);
                }
            }
        }
    }, [advance]);

    // Heartbeat — updates shared state to let other peers know the student smartboard is active and driving audio
    useEffect(() => {
        if (!state.sessionStarted || state.sessionEnded) return;
        const sendHeartbeat = () => {
            set({ smartboardActive: Date.now() });
        };
        sendHeartbeat();
        const intervalId = setInterval(sendHeartbeat, 3000);
        return () => clearInterval(intervalId);
    }, [state.sessionStarted, state.sessionEnded, set]);

    // Push local smartboard state changes to the WebSocket channel:
    // 1) Debounced by 120ms on every state mutation.
    // 2) Every 3 seconds as a robust WebSocket heartbeat fallback so late-joining/reconnecting phone remotes stay perfectly in sync.
    useEffect(() => {
        if (!sessionChannel?.send || !state.sessionStarted) return;
        const debounce = setTimeout(() => {
            const { annotations, ...lightweightState } = state;
            sessionChannel.send({
                type: "STATE_SYNC",
                role: "student-display",
                lessonId: lessonId,
                payload: lightweightState,
            });
        }, 120);
        return () => clearTimeout(debounce);
    }, [state, sessionChannel, lessonId]);

    useEffect(() => {
        if (!sessionChannel?.send || !state.sessionStarted) return;
        const intervalId = setInterval(() => {
            const { annotations, ...lightweightState } = state;
            sessionChannel.send({
                type: "STATE_SYNC",
                role: "student-display",
                lessonId: lessonId,
                payload: lightweightState,
                _heartbeat: true,
            });
        }, 3000);
        return () => clearInterval(intervalId);
    }, [sessionChannel, state, lessonId]);

    useEffect(() => {
        if (!state.sessionStarted) return;
        if (state.slideSelected === false) return;
        if (state.aiPaused || state.blanked) return;
        // Photo subslides are static (no audio, no fragments) — pause
        if ((state.subSlideIndex || 0) > 0) return;
        if (state.slideRenderMode === "full") return;
        // When autopilot is OFF, stop at the last fragment of the slide
        if (
            state.fragmentIndex >= totalFragments - 1 &&
            !state.autoPilotEnabled
        )
            return;

        // CRITICAL: Only the tab with active audio ownership should drive pacing and call advance()!
        // This prevents multiple open student tabs from setting competing timers and stepping on each other's pacing.
        if (!hasAudioOwnership) return;

        const slide = activeLesson?.slides?.[state.slideIndex];
        let entry = null;
        if (slide?.mode === "mcq_display" && state.activeMcqId) {
            if (state.revealMcqAnswer) {
                entry = findMcqExplanationStoryboardEntry(storyboard, state.activeMcqId);
            } else {
                entry = findMcqStoryboardEntry(storyboard, state.activeMcqId);
            }
        } else if (slide?.mode === "real_life_example" && state.activeExampleId) {
            entry = findExampleStoryboardEntry(storyboard, state.activeExampleId);
        }
        if (!entry) {
            entry = findStoryboardEntry(
                storyboard,
                state.slideIndex,
                state.fragmentIndex,
            );
        }

        // Reset our playback state only if we actually transitioned to a new slide or fragment.
        // This preserves playback completion flags (started, ended) if the session was paused/resumed
        // in the middle or at the end of the current fragment, avoiding stalling/timeouts.
        const wasSameFragment =
            playbackStateRef.current.slideIndex === state.slideIndex &&
            playbackStateRef.current.fragmentIndex === state.fragmentIndex &&
            playbackStateRef.current.activeMcqId === state.activeMcqId &&
            playbackStateRef.current.revealMcqAnswer === state.revealMcqAnswer &&
            playbackStateRef.current.activeExampleId === state.activeExampleId;

        if (!wasSameFragment) {
            playbackStateRef.current = {
                slideIndex: state.slideIndex,
                fragmentIndex: state.fragmentIndex,
                activeMcqId: state.activeMcqId,
                revealMcqAnswer: state.revealMcqAnswer,
                activeExampleId: state.activeExampleId,
                audioSrc: entry?.audio || null,
                started: false,
                ended: false,
            };
            remainingMsRef.current = entry?.durationMs || 0;
            playStartedAtRef.current = null;
        }

        // Clear any active timers from previous fragments
        if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
        if (autopilotTimerRef.current) clearTimeout(autopilotTimerRef.current);

        if (entry?.audio) {
            // When playing audio, we rely strictly on the native onEnded event to advance.
            // This is completely immune to network stutters, decoding lags, and stammers.
            // We schedule a safety fallback timer that is well beyond the remaining audio length.
            remainingMsRef.current = remainingMsRef.current || entry.durationMs || 10000;
            const fallbackDuration = remainingMsRef.current + 4000;

            playStartedAtRef.current = Date.now();
            autopilotTimerRef.current = setTimeout(() => {
                console.log(`[autopilot] Safety fallback duration reached (${fallbackDuration}ms). Advancing...`);
                advance();
            }, fallbackDuration);
        } else {
            // Fall back to standard static timer since there's no audio
            // Leverage remainingMsRef if we paused in the middle of a static slide
            const fallbackDuration = remainingMsRef.current || FRAGMENT_MS;
            playStartedAtRef.current = Date.now();
            autopilotTimerRef.current = setTimeout(() => {
                advance();
            }, fallbackDuration);
        }

        return () => {
            if (playStartedAtRef.current) {
                const elapsed = Date.now() - playStartedAtRef.current;
                remainingMsRef.current = Math.max(0, remainingMsRef.current - elapsed);
                playStartedAtRef.current = null;
            }
            if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
            if (autopilotTimerRef.current) clearTimeout(autopilotTimerRef.current);
        };
    }, [
        state.sessionStarted,
        state.slideSelected,
        state.aiPaused,
        state.blanked,
        state.slideRenderMode,
        state.slideIndex,
        state.fragmentIndex,
        state.autoPilotEnabled,
        state.subSlideIndex,
        totalFragments,
        storyboard,
        hasAudioOwnership,
        advance,
        activeLesson,
        state.activeMcqId,
        state.revealMcqAnswer,
        state.activeExampleId,
    ]);

    const tabId = useMemo(
        () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        [],
    );

    /* ---- single-owner audio claim via BroadcastChannel ---- */
    useEffect(() => {
        let channel = null;
        try {
            channel = new BroadcastChannel(`sparkl-student-audio-claim-${sessionIdFromUrl || "default"}-${lessonId}`);
        } catch {
            return;
        }
        const claim = () => {
            if (document.hidden) return;
            try {
                channel.postMessage({
                    type: "CLAIM",
                    tabId,
                    at: Date.now(),
                });
            } catch {
                /* noop */
            }
        };
        
        claim();
        
        // Periodically announce our claim to other tabs
        const claimInterval = setInterval(claim, 4000);
        
        let lastOtherClaim = 0;
        const checkOwnershipInterval = setInterval(() => {
            if (Date.now() - lastOtherClaim > 7000) {
                setHasAudioOwnership(true);
            }
        }, 2000);

        channel.onmessage = (e) => {
            if (e?.data?.type === "CLAIM" && e.data.tabId !== tabId) {
                lastOtherClaim = Date.now();
                setHasAudioOwnership(false);
            }
        };
        const onVis = () => {
            // When this tab becomes visible, re-claim ownership.
            if (!document.hidden) {
                claim();
                setHasAudioOwnership(true);
            }
        };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            clearInterval(claimInterval);
            clearInterval(checkOwnershipInterval);
            document.removeEventListener("visibilitychange", onVis);
            try {
                channel.close();
            } catch {
                /* noop */
            }
        };
    }, [tabId, sessionIdFromUrl, lessonId]);

    /* ---- auto-fullscreen when launched via Classroom Mode ---- */
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const classroomMode = !!params.get("sessionId");
        if (!classroomMode) return;

        let cancelled = false;

        const tryFs = async () => {
            if (isFullscreen()) return;
            const ok = await requestFullscreen();
            if (cancelled) return;
            if (!ok) setShowFsButton(true);
        };

        tryFs();

        const onMsg = (e) => {
            if (e?.data?.type === "sparkl:fullscreen") tryFs();
        };
        window.addEventListener("message", onMsg);

        let channel = null;
        try {
            channel = new BroadcastChannel("sparkl-classroom-session");
            channel.onmessage = (e) => {
                if (e?.data?.type === "STUDENT_FULLSCREEN") tryFs();
            };
        } catch {
            /* noop */
        }

        const onFsChange = () => {
            if (isFullscreen()) setShowFsButton(false);
        };
        document.addEventListener("fullscreenchange", onFsChange);
        document.addEventListener("webkitfullscreenchange", onFsChange);

        return () => {
            cancelled = true;
            window.removeEventListener("message", onMsg);
            document.removeEventListener("fullscreenchange", onFsChange);
            document.removeEventListener("webkitfullscreenchange", onFsChange);
            try {
                channel?.close();
            } catch {
                /* noop */
            }
        };
    }, []);

    const placeItem = (itemId, catId) =>
        set((p) => ({
            dragPlacements: { ...(p.dragPlacements || {}), [itemId]: catId },
            dragCheckResult: null,
        }));
    const pickupItem = (itemId) =>
        set((p) => {
            const next = { ...(p.dragPlacements || {}) };
            delete next[itemId];
            return { dragPlacements: next, dragCheckResult: null };
        });

    /* ---- Annotations — direct draw on the smartboard ---- */
    const annoKeyFor = (p) => {
        const slideId = activeLesson.slides[p.slideIndex]?.id;
        if (!slideId) return null;
        const sub = p.subSlideIndex || 0;
        return sub > 0 ? `${slideId}::photo-${sub}` : slideId;
    };
    const addStroke = (stroke) =>
        set((p) => {
            const key = annoKeyFor(p);
            if (!key) return p;
            const prev = p.annotations?.[key] || [];
            return {
                annotations: {
                    ...(p.annotations || {}),
                    [key]: [...prev, stroke],
                },
            };
        });
    const clearAnnotations = () =>
        set((p) => {
            const key = annoKeyFor(p);
            if (!key) return p;
            return {
                annotations: { ...(p.annotations || {}), [key]: [] },
            };
        });
    const undoStroke = () =>
        set((p) => {
            const key = annoKeyFor(p);
            if (!key) return p;
            const prev = p.annotations?.[key] || [];
            return {
                annotations: {
                    ...(p.annotations || {}),
                    [key]: prev.slice(0, -1),
                },
            };
        });
    const setAnnotationTool = (tool) => set({ annotationTool: tool });
    const setAnnotationColor = (color) => set({ annotationColor: color });

    const annotationTool = state.annotationTool || "off";
    const annotationColor = state.annotationColor || "#5BC2CC";

    return (
        <div className="w-screen h-screen overflow-hidden relative">
            <LessonStage
                state={state}
                showChrome
                playAudio={hasAudioOwnership}
                annotationProps={{
                    interactive: true,
                    tool: annotationTool,
                    color: annotationColor,
                    onAddStroke: addStroke,
                }}
                dragDropProps={{
                    interactive: true,
                    onPlace: placeItem,
                    onPickup: pickupItem,
                }}
                onAudioStarted={handleAudioStarted}
                onAudioEnded={handleAudioEnded}
                onAudioError={handleAudioError}
                onAudioMetadata={handleAudioMetadata}
            />

            {/* Annotation toolbar — sits unobtrusively at bottom-left of
                the smartboard. Tap pen/highlighter to start drawing. */}
            {state.slideSelected && !aiOverlayState && (
                <AnnotationToolbar
                    tool={annotationTool}
                    color={annotationColor}
                    onSetTool={setAnnotationTool}
                    onSetColor={setAnnotationColor}
                    onClear={clearAnnotations}
                    onUndo={undoStroke}
                />
            )}

            {showFsButton && (
                <button
                    data-testid="student-enter-fullscreen-btn"
                    onClick={async () => {
                        const ok = await requestFullscreen();
                        if (ok) setShowFsButton(false);
                    }}
                    className="fixed top-4 right-4 z-50 px-3 py-2 rounded-full bg-white/90 backdrop-blur border border-black/[0.06] shadow-[0_8px_24px_rgba(20,32,42,0.12)] hover:bg-white text-xs font-semibold text-[#14202a] flex items-center gap-1.5 transition-all active:scale-[0.97]"
                    title="Enter fullscreen"
                >
                    <Maximize2 className="w-3.5 h-3.5 text-[#2E8E9A]" />
                    Enter fullscreen
                </button>
            )}

            {/* Pairing QR for the Sparkl Teacher mobile app. Big card while
                no slide is selected (welcome screen), then a compact chip
                tucked in the corner once the lesson is rolling. */}
            {sessionIdFromUrl && !aiOverlayState && (
                <JoinQR
                    sessionId={sessionIdFromUrl}
                    compact={!!state.slideSelected}
                />
            )}

            {/* Bluetooth BLE Central connection button */}
            {!aiOverlayState && (
                <BluetoothPairButton onCommand={handleBluetoothCommand} />
            )}

            {/* ── AI Speaking Overlay ──────────────────────────────────────
                 When the teacher activates the AI voice assistant, we blur
                 the entire student display and bring the Mascot large at
                 the center. This signals to students that Sparkl is speaking.
                 Nothing on the lesson content can be interacted with during
                 this time (pointer-events:none on the content underneath).
                 The overlay fades + scales out smoothly when AI is done. */}
            <AnimatePresence>
                {aiOverlayState && (
                    <motion.div
                        key="ai-speaking-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                        className="fixed inset-0 z-[200] flex flex-col items-center justify-center"
                        style={{
                            backdropFilter: "blur(18px) saturate(140%)",
                            WebkitBackdropFilter: "blur(18px) saturate(140%)",
                            backgroundColor: "rgba(10, 16, 22, 0.55)",
                            pointerEvents: "all",
                        }}
                        data-testid="ai-speaking-overlay"
                    >
                        {/* Large centered Mascot */}
                        <motion.div
                            initial={{ scale: 0.45, opacity: 0, y: 60 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.5, opacity: 0, y: 40 }}
                            transition={{
                                duration: 1.1,
                                ease: [0.22, 1, 0.36, 1],
                                opacity: { duration: 0.6 },
                            }}
                            style={{ willChange: "transform" }}
                        >
                            <Mascot state={aiOverlayState} size="lg" />
                        </motion.div>

                        {/* Subtitle label beneath the mascot */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
                            className="mt-8 text-center"
                        >
                            <p
                                className="text-white/90 text-2xl font-semibold tracking-tight animate-pulse"
                                style={{
                                    textShadow: aiOverlayState === "listening"
                                        ? "0 2px 16px rgba(255,154,120,0.5)"
                                        : "0 2px 16px rgba(91,194,204,0.4)"
                                }}
                            >
                                {aiOverlayState === "idle" && "Sparkl is ready"}
                                {aiOverlayState === "listening" && "Sparkl is listening"}
                                {aiOverlayState === "thinking" && "Sparkl is thinking..."}
                                {aiOverlayState === "speaking" && "Sparkl is speaking"}
                            </p>
                            <p className="text-white/50 text-sm mt-2 tracking-wide">
                                {aiOverlayState === "idle" && "Your teacher is about to ask a question"}
                                {aiOverlayState === "listening" && "Shh... the teacher is asking a question"}
                                {aiOverlayState === "thinking" && "Formulating the best answer..."}
                                {aiOverlayState === "speaking" && "Listen carefully — your AI tutor is answering a question"}
                            </p>
                        </motion.div>

                        {/* Subtle ambient glow rings behind the mascot */}
                        <motion.div
                            className="absolute rounded-full pointer-events-none"
                            style={{
                                width: 520,
                                height: 520,
                                background: aiOverlayState === "listening"
                                    ? "radial-gradient(circle, rgba(255,154,120,0.12) 0%, rgba(255,154,120,0) 70%)"
                                    : "radial-gradient(circle, rgba(91,194,204,0.12) 0%, rgba(91,194,204,0) 70%)",
                                filter: "blur(30px)",
                            }}
                            animate={{ scale: [1, 1.08, 1] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>


        </div>
    );
};

/* ───────────────────── Annotation Toolbar ───────────────────── */
const COLORS = ["#5BC2CC", "#FFB547", "#FF5C5C", "#34C77B", "#A78BFA", "#14202A"];

const AnnotationToolbar = ({
    tool,
    color,
    onSetTool,
    onSetColor,
    onClear,
    onUndo,
}) => {
    const isActive = tool !== "off";
    return (
        <div
            data-testid="annotation-toolbar"
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-3 py-2 bg-white/90 backdrop-blur-xl border border-black/[0.06] rounded-2xl shadow-[0_18px_48px_rgba(20,32,42,0.18)]"
        >
            <ToolBtn
                active={tool === "pen"}
                color={tool === "pen" ? color : undefined}
                onClick={() => onSetTool(tool === "pen" ? "off" : "pen")}
                title="Pen"
                testid="annotation-tool-pen"
            >
                <Pen className="w-4 h-4" />
            </ToolBtn>
            <ToolBtn
                active={tool === "highlight"}
                color={tool === "highlight" ? color : undefined}
                onClick={() =>
                    onSetTool(tool === "highlight" ? "off" : "highlight")
                }
                title="Highlight"
                testid="annotation-tool-highlight"
            >
                <Highlighter className="w-4 h-4" />
            </ToolBtn>
            <ToolBtn
                active={tool === "eraser"}
                onClick={() =>
                    onSetTool(tool === "eraser" ? "off" : "eraser")
                }
                title="Eraser"
                testid="annotation-tool-eraser"
            >
                <Eraser className="w-4 h-4" />
            </ToolBtn>

            {(tool === "pen" || tool === "highlight") && (
                <div className="flex items-center gap-1 pl-2 ml-1 border-l border-black/[0.08]">
                    {COLORS.map((c) => (
                        <button
                            key={c}
                            data-testid={`annotation-color-${c}`}
                            onClick={() => onSetColor(c)}
                            aria-label={`Colour ${c}`}
                            className={`w-5 h-5 rounded-full transition-all ${
                                color === c
                                    ? "ring-2 ring-offset-2 ring-[#14202A] scale-110"
                                    : "hover:scale-110"
                            }`}
                            style={{ background: c }}
                        />
                    ))}
                </div>
            )}

            {isActive && (
                <div className="flex items-center gap-1 pl-2 ml-1 border-l border-black/[0.08]">
                    <ToolBtn
                        onClick={onUndo}
                        title="Undo last stroke"
                        testid="annotation-undo"
                    >
                        <X className="w-4 h-4" />
                    </ToolBtn>
                    <ToolBtn
                        onClick={onClear}
                        title="Clear all"
                        testid="annotation-clear"
                    >
                        <Trash2 className="w-4 h-4" />
                    </ToolBtn>
                </div>
            )}

        </div>
    );
};

const ToolBtn = ({ active, color, onClick, title, testid, children }) => (
    <button
        data-testid={testid}
        onClick={onClick}
        title={title}
        className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
            active ? "text-white" : "text-[#14202a] hover:bg-black/[0.04]"
        }`}
        style={
            active && color
                ? { background: color }
                : active
                  ? { background: "#14202A" }
                  : undefined
        }
    >
        {children}
    </button>
);

/**
 * Routes to the gaseous deck student page when `lessonId=gaseous-state`;
 * keeps the photosynthesis student stage untouched otherwise.
 */
const StudentDisplayRouter = () => {
    const lessonId = new URLSearchParams(window.location.search).get(
        "lessonId",
    );
    if (lessonId) setActiveLessonId(lessonId);
    if (lessonId === "gaseous-state") return <GaseousStudentDisplay />;
    return <StudentDisplay />;
};

export default StudentDisplayRouter;
